import { useEffect, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useUser from 'store/user/useUser';
import { IPatientInfoSmall } from 'apis/admin/type';
import { getSkillVideo } from 'apis/survey/index';
import ContentsHead from './ContentsHead';
import useNotification from 'hooks/useNotification';
import ContentsBody from './ContentsBody';
import useTable from 'hooks/useTable';
import { IUpedateSkillVideo } from 'apis/survey/type';
import { getSearchQuery } from 'utils/searchQuery';
import { Box } from '@mui/material';
import useClassSection from 'store/section/useClassSection';

interface Props {
  patientInfo: IPatientInfoSmall;
}

const VideoContents = ({ patientInfo }: Props) => {
  const { student_uuid: user_id } = useUser();
  const { search } = useLocation();
  const { onFail, onRequired } = useNotification();
  const { class_section_id } = useClassSection();

  const {
    page = 1,
    keyword,
    searchType,
    sort_method,
    size,
  } = getSearchQuery(search);

  const {
    list,
    setList,
    totalCount,
    setTotalCount,
    isLoading,
    setIsLoading,
    onPageChange,
    selected,
    setSelected,
  } = useTable<IUpedateSkillVideo>();

  const [onlyUploadedFiles, setOnlyUploadedFiles] = useState(false);

  const onGetList = useCallback(() => {
    setIsLoading(true);
    getSkillVideo({
      page: String(page),
      patient_id: String(patientInfo?.patient_id),
      user_id,
      keyword: keyword || '',
      searchType: searchType || '1',
      sort_method: sort_method || 1,
      size: size || 20,
      class_section_id,
    })
      .then(({ data }) => {
        const filteredList = onlyUploadedFiles
          ? data.student_video_info.filter(
              (student: any) =>
                student.student_video?.nursery_video1 ||
                student.student_video?.nursery_video2 ||
                student.student_video?.nursery_video3
            )
          : data.student_video_info;

        setList(filteredList);
        setTotalCount(data.count);
      })
      .catch(e => {
        const message = `가상환자 목록을 불러오는데 실패했습니다.`;
        onFail(message, e);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line
  }, [
    page,
    keyword,
    searchType,
    sort_method,
    onlyUploadedFiles,
    class_section_id,
    size,
    patientInfo.patient_id,
  ]);

  useEffect(onGetList, [onGetList]);

  return (
    <Box flex={2} display="flex" flexDirection="column" overflow="auto">
      <ContentsHead
        totalCount={totalCount}
        onRequired={onRequired}
        currentSearchType={searchType as any}
        currentSortType={sort_method as any}
        currentSize={size as any}
        setList={setList}
        setTotalCount={setTotalCount}
        setIsLoading={setIsLoading}
        setOnlyUploadedFiles={setOnlyUploadedFiles}
        patientInfo={patientInfo}
        onlyUploadedFiles={onlyUploadedFiles}
      />
      <ContentsBody
        totalCount={totalCount}
        list={list}
        isLoading={isLoading}
        page={Number(page) - 1}
        selected={selected}
        onSelected={setSelected}
        onPageChange={onPageChange}
      />
    </Box>
  );
};

export default VideoContents;
